import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CategoryIcon from '@mui/icons-material/Category';

const UploadSuccessModal = ({
  open,
  onClose,
  uploadData
}) => {
  if (!uploadData) return null;

  const { fileName, originalFormat, rowCount, headers, data } = uploadData;

  const getFormatChip = (format) => {
    const colorMap = {
      'csv': 'success',
      'acc': 'info',
      'rlc': 'warning',
      'rld': 'warning',
      'rps': 'secondary',
      'sec': 'primary',
      'tcf': 'success',
      'trn': 'info',
      'uln': 'default',
      'ult': 'default',
      'upn': 'default',
      'upt': 'default',
      'json': 'secondary',
      'xml': 'primary',
      'txt': 'default'
    };

    return (
      <Chip
        label={format.toUpperCase()}
        color={colorMap[format] || 'default'}
        size="small"
        sx={{ fontWeight: 'bold', ml: 1 }}
      />
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="upload-success-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 16px 24px',
          backgroundColor: '#f8f9fa',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon sx={{ 
              fontSize: 32, 
              color: 'success.main',
              backgroundColor: 'white',
              borderRadius: '50%',
              padding: '4px'
            }} />
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: '#333',
                fontSize: '20px',
                mb: 0.5
              }}>
                Upload Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your file has been processed and uploaded
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <CloseIcon sx={{ fontSize: 24, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#555'
            }
          }
        }}>
          {/* File Info */}
          <Box sx={{ padding: '16px 24px' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              padding: '12px 16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <DescriptionIcon sx={{ color: '#1344EB', fontSize: 20 }} />
              <Typography variant="body1" sx={{ 
                fontWeight: '500',
                color: '#333',
                flex: 1,
                wordBreak: 'break-word'
              }}>
                {fileName}
              </Typography>
              {getFormatChip(originalFormat)}
            </Box>
          </Box>

          <Divider sx={{ mx: 3 }} />

          {/* Upload Details */}
          <Box sx={{ padding: '16px 24px' }}>
          <Typography variant="h6" sx={{ 
            fontWeight: '600', 
            color: '#333',
            mb: 2,
            fontSize: '16px'
          }}>
            Processing Summary
          </Typography>
          
          <List dense sx={{ py: 0 }}>
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CategoryIcon sx={{ fontSize: 20, color: '#1344EB' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Original Format
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: '500' }}>
                      {originalFormat.toUpperCase()}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TableRowsIcon sx={{ fontSize: 20, color: '#1344EB' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Records Processed
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: '500' }}>
                      {rowCount.toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ViewColumnIcon sx={{ fontSize: 20, color: '#1344EB' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Columns Mapped
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: '500' }}>
                      {headers.length}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          </List>

          {/* Column Names */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Mapped Columns:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {headers.map((header, index) => (
                <Chip
                  key={index}
                  label={header}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    fontSize: '11px',
                    height: '24px',
                    color: '#666',
                    borderColor: '#ddd'
                  }}
                />
              ))}
            </Box>
          </Box>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{
          padding: '16px 24px 24px 24px',
          backgroundColor: '#fafafa',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          flexShrink: 0,
          borderTop: '1px solid #e0e0e0'
        }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: '#1344EB',
              color: 'white',
              textTransform: 'none',
              fontWeight: '500',
              borderRadius: '6px',
              minWidth: '100px',
              '&:hover': {
                backgroundColor: '#0D47A1'
              }
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadSuccessModal; 